/* ============================================================
   AOF — Price range slider (END Clothing-style)
   ------------------------------------------------------------
   Dual-handle range slider that drives the gte/lte price inputs.
   The inputs remain the source of truth -- Dawn's <price-range>
   web component already listens to them and submits the filter
   form. This slider:
     1. Reads initial min/max from the gte/lte inputs (or data-from / data-to)
     2. Updates input values on thumb drag
     3. Fires `input` + `change` events on the inputs so Dawn picks up the change
     4. Mirrors input changes back to the slider (user types a number directly)

   Visual spec (re-inspected from endclothing.com 2026-05-23):
     - 286px track, 2px height, black
     - 16x16 thumbs, white fill, 1.6px solid #1a1a1a, fully circular
   ============================================================ */

class AofPriceSlider extends HTMLElement {
  constructor() {
    super();
    this._min = parseFloat(this.dataset.min) || 0;
    this._max = parseFloat(this.dataset.max) || 100;
    if (this._max <= this._min) this._max = this._min + 1;

    this._from = this._parseInitial(this.dataset.from, this._min);
    this._to   = this._parseInitial(this.dataset.to,   this._max);

    this._track = this.querySelector('.aof-price-slider__track');
    this._range = this.querySelector('.aof-price-slider__range');
    this._thumbMin = this.querySelector('[data-handle="min"]');
    this._thumbMax = this.querySelector('[data-handle="max"]');

    this._minInput = document.querySelector(this.dataset.minInput);
    this._maxInput = document.querySelector(this.dataset.maxInput);

    this._draggingHandle = null;
    this._trackRect = null;

    // Bind once so the same function reference is added/removed.
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp   = this._onPointerUp.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onKeyDown     = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    // Pull the freshest value off the linked inputs in case Liquid disagreed.
    this._syncFromInputs();
    this._render();
    this._bind();
  }

  _parseInitial(raw, fallback) {
    if (raw == null || raw === '') return fallback;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : fallback;
  }

  _syncFromInputs() {
    if (this._minInput && this._minInput.value !== '') {
      const v = parseFloat(this._minInput.value);
      if (Number.isFinite(v)) this._from = this._clamp(v, this._min, this._max);
    }
    if (this._maxInput && this._maxInput.value !== '') {
      const v = parseFloat(this._maxInput.value);
      if (Number.isFinite(v)) this._to = this._clamp(v, this._min, this._max);
    }
    if (this._from > this._to) this._from = this._to;
  }

  _clamp(v, lo, hi) {
    return Math.min(Math.max(v, lo), hi);
  }

  _pctOf(value) {
    return ((value - this._min) / (this._max - this._min)) * 100;
  }

  _render() {
    const minPct = this._pctOf(this._from);
    const maxPct = this._pctOf(this._to);
    if (this._thumbMin) this._thumbMin.style.left = `${minPct}%`;
    if (this._thumbMax) this._thumbMax.style.left = `${maxPct}%`;
    if (this._range) {
      this._range.style.left = `${minPct}%`;
      this._range.style.right = `${100 - maxPct}%`;
    }
    if (this._thumbMin) {
      this._thumbMin.setAttribute('aria-valuemin', String(this._min));
      this._thumbMin.setAttribute('aria-valuemax', String(this._to));
      this._thumbMin.setAttribute('aria-valuenow', String(Math.round(this._from)));
    }
    if (this._thumbMax) {
      this._thumbMax.setAttribute('aria-valuemin', String(this._from));
      this._thumbMax.setAttribute('aria-valuemax', String(this._max));
      this._thumbMax.setAttribute('aria-valuenow', String(Math.round(this._to)));
    }
  }

  _bind() {
    // Pointer drag -- works for mouse, touch, and pen.
    [this._thumbMin, this._thumbMax].forEach(thumb => {
      if (!thumb) return;
      thumb.addEventListener('pointerdown', this._onPointerDown);
      thumb.addEventListener('keydown', this._onKeyDown);
    });

    // Listen to input changes to mirror them back to the slider.
    if (this._minInput) {
      this._minInput.addEventListener('change', () => {
        this._syncFromInputs();
        this._render();
      });
    }
    if (this._maxInput) {
      this._maxInput.addEventListener('change', () => {
        this._syncFromInputs();
        this._render();
      });
    }
  }

  _onPointerDown(event) {
    event.preventDefault();
    this._draggingHandle = event.currentTarget.dataset.handle;
    this._trackRect = this._track.getBoundingClientRect();
    this._activeThumb = event.currentTarget;
    try { this._activeThumb.setPointerCapture(event.pointerId); } catch (e) {}
    this._activeThumb.addEventListener('pointermove', this._onPointerMove);
    this._activeThumb.addEventListener('pointerup', this._onPointerUp);
    this._activeThumb.addEventListener('pointercancel', this._onPointerUp);
  }

  _onPointerMove(event) {
    if (!this._draggingHandle || !this._trackRect) return;
    const ratio = (event.clientX - this._trackRect.left) / this._trackRect.width;
    const value = this._clamp(this._min + ratio * (this._max - this._min), this._min, this._max);

    if (this._draggingHandle === 'min') {
      this._from = Math.min(value, this._to);
    } else {
      this._to = Math.max(value, this._from);
    }
    this._render();
    this._writeInputs(false); // live update of input display only, no form submit
  }

  _onPointerUp(event) {
    this._draggingHandle = null;
    this._trackRect = null;
    if (this._activeThumb) {
      try { this._activeThumb.releasePointerCapture(event.pointerId); } catch (e) {}
      this._activeThumb.removeEventListener('pointermove', this._onPointerMove);
      this._activeThumb.removeEventListener('pointerup', this._onPointerUp);
      this._activeThumb.removeEventListener('pointercancel', this._onPointerUp);
      this._activeThumb = null;
    }
    // Final write — fire `change` so Dawn submits the filter form.
    this._writeInputs(true);
  }

  _onKeyDown(event) {
    const handle = event.currentTarget.dataset.handle;
    const step = (this._max - this._min) / 100;
    let delta = 0;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown': delta = -step; break;
      case 'ArrowRight':
      case 'ArrowUp':   delta = step; break;
      case 'PageDown':  delta = -step * 10; break;
      case 'PageUp':    delta = step * 10; break;
      case 'Home':      delta = -Infinity; break;
      case 'End':       delta = Infinity; break;
      default: return;
    }
    event.preventDefault();
    if (handle === 'min') {
      this._from = this._clamp(this._from + delta, this._min, this._to);
    } else {
      this._to = this._clamp(this._to + delta, this._from, this._max);
    }
    this._render();
    this._writeInputs(true);
  }

  _writeInputs(commit) {
    if (this._minInput) {
      const v = Math.round(this._from);
      // Only set value if it actually changed -- avoids re-render loops.
      if (this._minInput.value !== String(v)) {
        this._minInput.value = String(v);
        this._minInput.dispatchEvent(new Event('input', { bubbles: true }));
        if (commit) this._minInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (commit) {
        this._minInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (this._maxInput) {
      const v = Math.round(this._to);
      if (this._maxInput.value !== String(v)) {
        this._maxInput.value = String(v);
        this._maxInput.dispatchEvent(new Event('input', { bubbles: true }));
        if (commit) this._maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (commit) {
        this._maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}

if (!customElements.get('aof-price-slider')) {
  customElements.define('aof-price-slider', AofPriceSlider);
}
